/* ==========================================================================
   Free Shipping Progress Bar — Cosmo 9.x custom component
   - Live-updates on every cart change (theme events, Shopify AJAX calls from
     the theme or any app), recalculates in the shopper's currency.
   - Dynamic note based on the number of items in the cart.
   - Colour shifting + confetti celebration when the goal is reached.
   ========================================================================== */
(() => {
  if (customElements.get("free-shipping-bar")) return;

  const STORAGE_ACHIEVED = "fsb:achieved";
  const STORAGE_CELEBRATED = "fsb:celebrated";
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  /* ---------------- Shared cart feed ---------------- */
  const bus = new EventTarget();
  const nativeFetch = window.fetch.bind(window);
  let latestCart = null;
  let refreshTimer = null;

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
    },
  };

  const isFullCart = (data) =>
    data &&
    typeof data === "object" &&
    typeof data.total_price === "number" &&
    typeof data.item_count === "number" &&
    Array.isArray(data.items);

  const publishCart = (cart) => {
    latestCart = cart;
    bus.dispatchEvent(new CustomEvent("cart", { detail: cart }));
  };

  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    (window.FoxThemeSettings && window.FoxThemeSettings.routes && window.FoxThemeSettings.routes.root) ||
    "/";

  const fetchCart = async () => {
    try {
      const res = await nativeFetch(`${rootUrl()}cart.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      publishCart(await res.json());
    } catch (e) {
      /* network hiccup — the next cart event will retry */
    }
  };

  const requestCartRefresh = (delay = 120) => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(fetchCart, delay);
  };

  // Catch cart changes made by the theme *or* by apps (upsells, bundles, etc.)
  if (!window.__fsbCartWatch) {
    window.__fsbCartWatch = true;

    window.fetch = function (...args) {
      const promise = nativeFetch(...args);
      try {
        const input = args[0];
        const url = typeof input === "string" ? input : (input && input.url) || "";
        if (CART_MUTATION.test(url)) promise.then(() => requestCartRefresh(), () => {});
      } catch (e) { /* never block the original request */ }
      return promise;
    };

    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__fsbUrl = url;
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (CART_MUTATION.test(String(this.__fsbUrl || ""))) {
        this.addEventListener("load", () => requestCartRefresh());
      }
      return xhrSend.apply(this, arguments);
    };

    ["cart:updated", "cart:refresh", "cart:change", "cart:build"].forEach((name) =>
      document.addEventListener(name, (event) => {
        const cart = event.detail && event.detail.cart;
        if (isFullCart(cart)) publishCart(cart);
        else requestCartRefresh();
      })
    );

    // Back/forward cache: the cart may have changed in another tab
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) requestCartRefresh(0);
    });
  }

  /* ---------------- Helpers ---------------- */
  const MONEY_STYLES = {
    amount: [2, ",", "."],
    amount_no_decimals: [0, ",", "."],
    amount_with_comma_separator: [2, ".", ","],
    amount_no_decimals_with_comma_separator: [0, ".", ","],
    amount_with_apostrophe_separator: [2, "'", "."],
    amount_with_space_separator: [2, " ", ","],
    amount_no_decimals_with_space_separator: [0, " ", ","],
    amount_with_period_and_space_separator: [2, " ", "."],
  };

  const formatMoney = (cents, format, trimZeros) => {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    const fmt = format || "${{amount}}";
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const style = MONEY_STYLES[match ? match[1] : "amount"] || MONEY_STYLES.amount;
    let precision = style[0];
    if (trimZeros && value % 100 === 0) precision = 0;
    const [whole, frac] = (value / 100).toFixed(precision).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, style[1]);
    const out = frac ? grouped + style[2] + frac : grouped;
    return match ? fmt.replace(match[0], out) : out;
  };

  const parseColor = (input) => {
    const str = String(input || "").trim();
    if (str.startsWith("#")) {
      let hex = str.slice(1);
      if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
      const n = parseInt(hex.slice(0, 6), 16);
      if (Number.isNaN(n)) return null;
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const nums = str.match(/[\d.]+/g);
    return nums && nums.length >= 3 ? nums.slice(0, 3).map(Number) : null;
  };

  const mixColors = (from, to, t) => {
    const a = parseColor(from);
    const b = parseColor(to);
    if (!a || !b) return to || from;
    return (
      "#" +
      a.map((v, i) => Math.round(v + (b[i] - v) * t).toString(16).padStart(2, "0")).join("")
    );
  };

  const fillTemplate = (template, values) =>
    String(template || "")
      .split("[remaining]").join(values.remaining)
      .split("[goal]").join(values.goal)
      .split("[count]").join(values.count);

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------- Confetti ---------------- */
  const runConfetti = ({ style, amount, colors, origin, area }) => {
    const canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    Object.assign(canvas.style, {
      position: "fixed",
      inset: "0",
      width: "100vw",
      height: "100vh",
      pointerEvents: "none",
      zIndex: "2147483000",
    });
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rand = (min, max) => min + Math.random() * (max - min);
    const pick = (list) => list[Math.floor(Math.random() * list.length)];
    const palette = colors && colors.length ? colors : ["#c97c5d", "#f0b199", "#2f8f5b", "#ffd166"];
    const shapes = ["rect", "rect", "circle", "ribbon"];
    const particles = [];

    const spawn = (x, y, angle, speed, extra = {}) => {
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: rand(6, 10),
        h: rand(4, 7),
        shape: pick(shapes),
        color: pick(palette),
        rot: rand(0, Math.PI * 2),
        vr: rand(-0.18, 0.18),
        tilt: rand(0, Math.PI * 2),
        vt: rand(0.06, 0.16),
        drag: rand(0.965, 0.985),
        gravity: rand(0.2, 0.32),
        life: 0,
        ttl: rand(150, 230),
        delay: 0,
        ...extra,
      });
    };

    const count = Math.max(20, Math.min(400, Number(amount) || 140));

    if (style === "cannons") {
      const half = Math.round(count / 2);
      for (let i = 0; i < count; i++) {
        const left = i < half;
        const x = left ? area.left + 8 : area.right - 8;
        const y = area.bottom - 12;
        const base = rand(-1.35, -0.85); // up and inwards
        const angle = left ? base : Math.PI - base;
        spawn(x, y, angle, rand(12, 21), { delay: rand(0, 8) });
      }
    } else if (style === "rain") {
      for (let i = 0; i < count; i++) {
        spawn(rand(area.left, area.right), area.top - rand(10, 60), Math.PI / 2, rand(1.5, 4), {
          vx: rand(-1.2, 1.2),
          gravity: rand(0.04, 0.08),
          drag: 0.992,
          ttl: rand(220, 320),
          delay: rand(0, 70),
        });
      }
    } else {
      // burst — pops out of the progress bar, then drifts down over the cart
      for (let i = 0; i < count; i++) {
        const x = rand(origin.left, origin.right);
        const angle = -Math.PI / 2 + rand(-Math.PI * 0.85, Math.PI * 0.85);
        spawn(x, origin.y, angle, rand(3, 10), { gravity: rand(0.12, 0.2), delay: rand(0, 6) });
      }
    }

    let last = performance.now();
    const frame = (now) => {
      const f = Math.min(3, (now - last) / 16.67);
      last = now;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = 0;
      for (const p of particles) {
        if (p.life >= p.ttl) continue;
        if (p.delay > 0) {
          p.delay -= f;
          alive++;
          continue;
        }
        p.vx *= Math.pow(p.drag, f);
        p.vy = p.vy * Math.pow(p.drag, f) + p.gravity * f;
        p.x += p.vx * f;
        p.y += p.vy * f;
        p.rot += p.vr * f;
        p.tilt += p.vt * f;
        p.life += f;
        if (p.y > window.innerHeight + 40) {
          p.life = p.ttl;
          continue;
        }
        alive++;

        const fade = p.ttl - p.life < 40 ? Math.max(0, (p.ttl - p.life) / 40) : 1;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, Math.cos(p.tilt));
        ctx.fillStyle = p.color;
        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.h / 2 + 1, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "ribbon") {
          ctx.fillRect(-1.5, -p.w, 3, p.w * 2);
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }

      if (alive > 0) {
        requestAnimationFrame(frame);
      } else {
        window.removeEventListener("resize", resize);
        canvas.remove();
      }
    };
    requestAnimationFrame(frame);
  };

  /* ---------------- Component ---------------- */
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector("[data-fsb-config]");
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.warn("[free-shipping-bar] Invalid config", e);
        return;
      }

      this.els = {
        message: this.querySelector("[data-fsb-message]"),
        track: this.querySelector("[data-fsb-track]"),
        percent: this.querySelector("[data-fsb-percent]"),
        goal: this.querySelector("[data-fsb-goal]"),
        note: this.querySelector("[data-fsb-note]"),
        noteText: this.querySelector("[data-fsb-note-text]"),
      };

      this.drawer = this.closest("drawer-component");
      this.pendingCelebration = false;
      this.lastMessage = this.els.message ? this.els.message.innerHTML : "";
      this.lastNote = this.els.noteText ? this.els.noteText.innerHTML : "";
      this.lastRemaining = null;

      this.onCart = (event) => this.update(event.detail);
      bus.addEventListener("cart", this.onCart);

      // Cosmo / FoxEcom pub-sub
      if (window.FoxThemeEvents && typeof window.FoxThemeEvents.subscribe === "function") {
        this.unsubscribers = ["cart-update"].map((name) =>
          window.FoxThemeEvents.subscribe(name, (state) => {
            if (isFullCart(state)) publishCart(state);
            else requestCartRefresh(0);
          })
        );
        const unsubOpen = window.FoxThemeEvents.subscribe("ON_CART_DRAWER_OPEN", () => {
          if (this.pendingCelebration) this.celebrate();
        });
        this.unsubscribers.push(unsubOpen);
      }

      // Fire a pending celebration as soon as the drawer slides open
      if (this.drawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (this.pendingCelebration && this.drawer.hasAttribute("open")) {
            setTimeout(() => this.celebrate(), 520);
          }
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ["open"] });
      }

      this.update(latestCart || this.config.initial, { initial: true });
    }

    disconnectedCallback() {
      bus.removeEventListener("cart", this.onCart);
      (this.unsubscribers || []).forEach((unsub) => typeof unsub === "function" && unsub());
      if (this.drawerObserver) this.drawerObserver.disconnect();
    }

    get rate() {
      const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return rate > 0 ? rate : 1;
    }

    itemsLabel(n) {
      return `${n} ${n === 1 ? this.config.itemSingular : this.config.itemPlural}`;
    }

    compute(cart) {
      const c = this.config;
      const count = Number(cart.item_count) || 0;
      let goal;
      let current;
      if (c.goalType === "quantity") {
        goal = Math.max(1, Math.round(Number(c.goalQuantity) || 1));
        current = count;
      } else {
        // Exact threshold in cents from the store currency (e.g. 4495 for $44.95),
        // converted for Shopify Markets currencies. Integer maths, so no float drift.
        const baseCents = Number.isFinite(Number(c.goalCents))
          ? Math.round(Number(c.goalCents))
          : Math.round((Number(c.goalAmount) || 0) * 100);
        goal = this.rate === 1 ? baseCents : Math.round(baseCents * this.rate);
        const basis = c.basis === "original" ? cart.original_total_price : cart.total_price;
        current = Number(basis != null ? basis : cart.total_price) || 0;
      }
      const remaining = Math.max(0, goal - current);
      const percent = goal > 0 ? Math.max(0, Math.min(100, (current / goal) * 100)) : 100;
      const achieved = count > 0 && current >= goal;
      return { count, goal, current, remaining, percent, achieved };
    }

    update(cart, { initial = false } = {}) {
      if (!cart || !this.config) return;
      const c = this.config;
      const s = this.compute(cart);

      const isQty = c.goalType === "quantity";
      const remainingText = isQty
        ? this.itemsLabel(s.remaining)
        : formatMoney(s.remaining, c.moneyFormat, c.trimZeros);
      const goalText = isQty ? this.itemsLabel(s.goal) : formatMoney(s.goal, c.moneyFormat, c.trimZeros);
      const values = {
        remaining: `<span class="fsb__amount">${remainingText}</span>`,
        goal: `<span class="fsb__goal">${goalText}</span>`,
        count: String(s.count),
      };

      // Visibility / state classes
      const empty = s.count === 0;
      this.hidden = empty && !c.showWhenEmpty;
      this.classList.toggle("is-empty", empty);
      this.classList.toggle("is-achieved", s.achieved);

      // Bar + colours
      this.style.setProperty("--fsb-progress", `${s.percent.toFixed(2)}%`);
      if (c.colors && c.colors.mode !== "solid") {
        this.style.setProperty("--fsb-fill-mix", mixColors(c.colors.start, c.colors.end, s.percent / 100));
      }
      if (this.els.track) {
        this.els.track.setAttribute("aria-valuenow", String(Math.round(s.percent)));
      }
      if (this.els.percent) this.els.percent.textContent = `${Math.round(s.percent)}%`;
      if (this.els.goal) this.els.goal.textContent = goalText;

      // Headline message
      let template = c.messages.progress;
      if (empty && c.messages.empty) template = c.messages.empty;
      else if (s.achieved) template = c.messages.success;
      const message = fillTemplate(template, values);
      if (this.els.message && message !== this.lastMessage) {
        this.els.message.innerHTML = message;
        if (!initial) this.animateIn(this.els.message);
        this.lastMessage = message;
      } else if (!initial && this.lastRemaining !== null && this.lastRemaining !== s.remaining) {
        const amount = this.els.message && this.els.message.querySelector(".fsb__amount");
        if (amount) this.animateIn(amount);
      }
      this.lastRemaining = s.remaining;

      // Dynamic note based on items in cart
      if (this.els.note && c.notes && c.notes.enabled) {
        let noteTemplate = "";
        if (!empty) {
          let best = 0;
          (c.notes.tiers || []).forEach((tier) => {
            const min = Number(tier.min) || 0;
            if (tier.text && s.count >= min && min >= best) {
              noteTemplate = tier.text;
              best = min;
            }
          });
          if (s.achieved && c.notes.success) noteTemplate = c.notes.success;
        }
        const note = fillTemplate(noteTemplate, values);
        this.els.note.hidden = !note;
        if (note && note !== this.lastNote) {
          this.els.noteText.innerHTML = note;
          if (!initial) this.animateIn(this.els.note);
        }
        this.lastNote = note;
      }

      // Celebration — only on the transition from "not yet" to "unlocked"
      const wasAchieved = initial ? storage.get(STORAGE_ACHIEVED) === "1" : this.achieved;
      const knownBefore = initial ? storage.get(STORAGE_ACHIEVED) !== null : this.achieved !== undefined;
      if (s.achieved && knownBefore && !wasAchieved) {
        setTimeout(() => this.celebrate(), initial ? 300 : 650);
      }
      this.achieved = s.achieved;
      storage.set(STORAGE_ACHIEVED, s.achieved ? "1" : "0");
    }

    animateIn(el) {
      el.classList.remove("fsb-anim-in");
      void el.offsetWidth; // restart the animation
      el.classList.add("fsb-anim-in");
    }

    isOnScreen() {
      if (this.drawer && !this.drawer.hasAttribute("open")) return false;
      const rect = this.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      );
    }

    celebrate() {
      // The drawer may have been re-rendered — hand off to the live instance
      if (!this.isConnected) {
        const live = document.querySelector("free-shipping-bar");
        if (live && live !== this && live.config) {
          live.achieved = true;
          live.celebrate();
        }
        return;
      }
      if (!this.achieved) {
        this.pendingCelebration = false;
        return;
      }
      if (!this.isOnScreen()) {
        this.pendingCelebration = true;
        return;
      }
      this.pendingCelebration = false;

      if (this.config.pulse) {
        this.classList.remove("is-celebrating");
        void this.offsetWidth;
        this.classList.add("is-celebrating");
        clearTimeout(this.celebrateTimer);
        this.celebrateTimer = setTimeout(() => this.classList.remove("is-celebrating"), 2400);
      }

      const confetti = this.config.confetti || {};
      if (!confetti.enabled || prefersReducedMotion()) return;
      if (confetti.once && storage.get(STORAGE_CELEBRATED) === "1") return;
      storage.set(STORAGE_CELEBRATED, "1");

      const bar = (this.els.track || this).getBoundingClientRect();
      const container = (this.drawer && this.drawer.querySelector(".f-drawer__content")) || document.documentElement;
      const areaRect = container.getBoundingClientRect();
      const area = {
        left: Math.max(0, areaRect.left),
        right: Math.min(window.innerWidth, areaRect.right),
        top: Math.max(0, areaRect.top),
        bottom: Math.min(window.innerHeight, areaRect.bottom),
      };

      runConfetti({
        style: confetti.style,
        amount: confetti.amount,
        colors: (confetti.colors || []).filter((col) => parseColor(col)),
        origin: { left: bar.left, right: bar.right, y: bar.top + bar.height / 2 },
        area,
      });
    }
  }

  customElements.define("free-shipping-bar", FreeShippingBar);

  // Expose a tiny API for debugging / other scripts
  window.FreeShippingBar = { refresh: () => requestCartRefresh(0) };
})();
